import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import type { FAQ } from "@ckgo2/shared/types";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import { useEditPage } from "@/hooks/useEditPage";
import ErrorBanner from "@/components/common/ErrorBanner";
import { RichTextEditor } from "@/components/editor/RichTextEditor";
import { Controller } from "react-hook-form";

interface FaqFormData {
  question: string;
  answer: string;
  topic: string;
  topicSortOrder: number;
  sortOrder: number;
  status: string;
}

export default function FaqEditPage() {
  // Fetch existing FAQs for topic suggestions
  const { data: allFaqsData } = useQuery({
    queryKey: ["faqs"],
    queryFn: () => api.get<ApiResponse<FAQ[]>>("/api/admin/faqs"),
  });

  const { isNew, isLoading, form, saveMutation, onSubmit, itemData } = useEditPage<
    FaqFormData,
    FAQ
  >({
    resourceKey: "faq",
    listKey: "faqs",
    apiPath: "/api/admin/faqs",
    defaultValues: {
      question: "",
      answer: "",
      topic: "",
      topicSortOrder: 0,
      sortOrder: 0,
      status: "published",
    },
    navigateTo: "/faqs",
    transformOnLoad: (f) => ({
      question: f.question || "",
      answer: f.answer || "",
      topic: f.topic || "",
      topicSortOrder: f.topicSortOrder || 0,
      sortOrder: f.sortOrder || 0,
      status: f.status || "published",
    }),
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = form;

  // Extract unique topics from existing FAQs
  const existingTopics = [...new Set((allFaqsData?.data || []).map((f) => f.topic))].filter(
    Boolean,
  );

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/faqs" className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700">
            <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isNew ? "New FAQ" : "Edit FAQ"}
            </h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              {isNew ? "Create a new FAQ item" : `Editing: ${itemData?.question?.slice(0, 50)}...`}
            </p>
          </div>
        </div>
        <button type="submit" form="faq-form" disabled={isSubmitting} className="btn-primary">
          {isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-5 w-5" />
              Save
            </>
          )}
        </button>
      </div>

      <ErrorBanner error={saveMutation.error} />

      <form id="faq-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="card space-y-4">
          {/* Topic */}
          <div>
            <label className="label">Topic</label>
            <input
              {...register("topic", { required: "Topic is required" })}
              className="input"
              placeholder="e.g. Booking & Payments"
              list="topic-suggestions"
            />
            <datalist id="topic-suggestions">
              {existingTopics.map((t) => (
                <option key={t} value={t} />
              ))}
            </datalist>
            {errors.topic && <p className="mt-1 text-sm text-red-500">{errors.topic.message}</p>}
          </div>

          {/* Question */}
          <div>
            <label className="label">Question</label>
            <input
              {...register("question", { required: "Question is required" })}
              className="input"
              placeholder="e.g. How do I book a trip?"
            />
            {errors.question && (
              <p className="mt-1 text-sm text-red-500">{errors.question.message}</p>
            )}
          </div>

          {/* Answer */}
          <div>
            <label className="label">Answer</label>
            <Controller
              name="answer"
              control={control}
              rules={{ required: "Answer is required" }}
              render={({ field }) => (
                <RichTextEditor
                  content={field.value || ""}
                  onChange={field.onChange}
                  placeholder="Write the answer..."
                />
              )}
            />
            {errors.answer && <p className="mt-1 text-sm text-red-500">{errors.answer.message}</p>}
          </div>

          {/* Sort orders */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Topic sort order</label>
              <input
                type="number"
                {...register("topicSortOrder", { valueAsNumber: true })}
                className="input"
              />
            </div>
            <div>
              <label className="label">Sort order (within topic)</label>
              <input
                type="number"
                {...register("sortOrder", { valueAsNumber: true })}
                className="input"
              />
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="label">Status</label>
            <select {...register("status")} className="input">
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
          </div>
        </div>
      </form>
    </div>
  );
}
